import {
  forwardRef,
  PropsWithChildren,
  MutableRefObject,
  ReactElement,
  useImperativeHandle,
} from 'react';
import { noop } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';

import { PartialDeep } from 'type-fest';

import { FormContext } from '../../contexts/FormContext';

import { FormCheckbox } from './FormCheckbox/FormCheckbox';
import { FormDatepicker } from './FormDatepicker/FormDatepicker';
import { FormPassword } from './FormPassword/FormPassword';
import { FormSection } from './FormSection/FormSection';
import { FormSubmit } from './FormSubmit/FormSubmit';
import { FormText } from './FormText/FormText';
import { FormRadioGroup } from './FormRadioGroup/FormRadioGroup';
import { FormCheckboxGroup } from './FormCheckboxGroup/FormCheckboxGroup';
import { FormActions } from './FormActions/FormActions';
import { FormSelect } from './FormSelect/FormSelect';
import { FormCheckboxToggle } from './FormCheckboxToggle/FormCheckboxToggle';
import { FormPlain } from './FormPlain/FormPlain';
import { FormTextarea } from './FormTextarea/FormTextarea';
import { FormHint } from './FormHint/FormHint';
import { FormRadioToggle } from './FormRadioToggle/FormRadioToggle';
import { FormDate } from './FormDate/FormDate';
import { FormTabIndexProvider } from './FormTabIndexProvider';
import { FormInstance } from './Form.types';

export type FormProps<T extends object = {}> = PropsWithChildren<{
  onSubmit?: (v: T) => void;
  defaultValues?: PartialDeep<T>;
  ref?: MutableRefObject<FormInstance>;
}>;

interface FormComponent {
  <T extends object = {}>(props: FormProps<T>, ref: FormInstance): ReactElement;
  Actions: typeof FormActions;
  Checkbox: typeof FormCheckbox;
  CheckboxGroup: typeof FormCheckboxGroup;
  CheckboxToggle: typeof FormCheckboxToggle;
  Date: typeof FormDate;
  Datepicker: typeof FormDatepicker;
  Hint: typeof FormHint;
  Password: typeof FormPassword;
  Plain: typeof FormPlain;
  RadioGroup: typeof FormRadioGroup;
  RadioToggle: typeof FormRadioToggle;
  Section: typeof FormSection;
  Select: typeof FormSelect;
  Submit: typeof FormSubmit;
  Text: typeof FormText;
  Textrea: typeof FormTextarea;
}

export const Form = forwardRef<FormInstance, FormProps>(
  ({ children, defaultValues, onSubmit = noop }, ref) => {
    const methods = useForm({ defaultValues });

    const submit = methods.handleSubmit(onSubmit);

    const hasUnsavedChanges = (() => {
      const value = [
        methods.formState.isDirty && !methods.formState.isSubmitted,
        !!Object.keys(methods.formState.errors).length,
      ].includes(true);
      return () => value;
    })();

    const setErrors = (errors: Record<string, string>) => {
      Object.entries(errors).forEach(([path, message]) => {
        // @ts-ignore
        methods.setError(path, { message });
      });
    };

    useImperativeHandle(ref, () => ({
      setErrors,
      hasUnsavedChanges,
    }));

    return (
      <FormProvider {...methods}>
        <FormTabIndexProvider>
          <FormContext.Provider value={{ submit }}>
            {children}
          </FormContext.Provider>
        </FormTabIndexProvider>
      </FormProvider>
    );
  }
) as unknown as FormComponent;

Form.Actions = FormActions;
Form.Checkbox = FormCheckbox;
Form.CheckboxGroup = FormCheckboxGroup;
Form.CheckboxToggle = FormCheckboxToggle;
Form.Date = FormDate;
Form.Datepicker = FormDatepicker;
Form.Hint = FormHint;
Form.Password = FormPassword;
Form.Plain = FormPlain;
Form.RadioGroup = FormRadioGroup;
Form.RadioToggle = FormRadioToggle;
Form.Section = FormSection;
Form.Select = FormSelect;
Form.Submit = FormSubmit;
Form.Text = FormText;
Form.Textrea = FormTextarea;
